import api from './api';
import {
  ENDPOINT_AUTH_TWO_FACTOR,
  ENDPOINT_AUTH_TWO_FACTOR_CONFIRM,
  ENDPOINT_AUTH_TWO_FACTOR_DEVICES,
  ENDPOINT_AUTH_TWO_FACTOR_REAUTH,
  ENDPOINT_AUTH_TWO_FACTOR_REAUTH_VERIFY,
  ENDPOINT_AUTH_TWO_FACTOR_RECOVERY_CODES,
  TWO_FACTOR_STATUS_DEFAULTS,
} from '../../utils/constants';

const getTwoFactorStatus = (builder, {path}) =>
  builder.query({
    providesTags: ['TWO_FACTOR'],
    query: () => path,
    transformResponse: response => ({
      ...TWO_FACTOR_STATUS_DEFAULTS,
      ...response,
    }),
  });

const startTwoFactorEnrolment = (builder, {path}) =>
  builder.mutation({
    query: ({channel}) => ({
      url: path,
      method: 'POST',
      body: {channel},
    }),
  });

const confirmTwoFactorEnrolment = (builder, {confirmPath}) =>
  builder.mutation({
    invalidatesTags: ['TWO_FACTOR'],
    query: ({challenge, code}) => ({
      url: confirmPath,
      method: 'POST',
      body: {challenge, code},
    }),
  });

const disableTwoFactor = (builder, {path}) =>
  builder.mutation({
    invalidatesTags: ['TWO_FACTOR'],
    query: ({password}) => ({
      url: path,
      method: 'DELETE',
      body: {password},
    }),
  });

const forgetTwoFactorDevices = (builder, {devicesPath}) =>
  builder.mutation({
    invalidatesTags: ['TWO_FACTOR'],
    query: () => ({
      url: devicesPath,
      method: 'DELETE',
    }),
  });

/**
 * Opens a step-up challenge. No body: the server sends the code to the
 * enrolled channel, never a requested one.
 */
const startTwoFactorReauth = (builder, {reauthPath}) =>
  builder.mutation({
    query: () => ({
      url: reauthPath,
      method: 'POST',
    }),
  });

/**
 * Confirms a step-up. On success the account holds a fresh step-up for the
 * server's window, so the blocked change can be retried.
 */
const verifyTwoFactorReauth = (builder, {reauthVerifyPath}) =>
  builder.mutation({
    query: ({challenge, code}) => ({
      url: reauthVerifyPath,
      method: 'POST',
      body: {challenge, code},
    }),
  });

/**
 * Remaining unused recovery codes. A count only — the codes themselves are
 * shown once at enrolment or regeneration and never returned again. Tagged so
 * it refreshes alongside the rest of the status view.
 */
const getRecoveryCodeCount = (builder, {recoveryCodesPath}) =>
  builder.query({
    providesTags: ['TWO_FACTOR'],
    query: () => recoveryCodesPath,
  });

/**
 * Mints a fresh set, retiring the old one, and returns the new codes once.
 * Step-up gated like the other state-changing endpoints: a 423 comes back
 * when the account must re-prove its factor first.
 */
const regenerateRecoveryCodes = (builder, {recoveryCodesPath}) =>
  builder.mutation({
    invalidatesTags: ['TWO_FACTOR'],
    query: () => ({
      url: recoveryCodesPath,
      method: 'POST',
    }),
  });

export const config = {
  path: `/${ENDPOINT_AUTH_TWO_FACTOR}`,
  confirmPath: `/${ENDPOINT_AUTH_TWO_FACTOR_CONFIRM}`,
  devicesPath: `/${ENDPOINT_AUTH_TWO_FACTOR_DEVICES}`,
  reauthPath: `/${ENDPOINT_AUTH_TWO_FACTOR_REAUTH}`,
  reauthVerifyPath: `/${ENDPOINT_AUTH_TWO_FACTOR_REAUTH_VERIFY}`,
  recoveryCodesPath: `/${ENDPOINT_AUTH_TWO_FACTOR_RECOVERY_CODES}`,
};

const twoFactorApi = api.injectEndpoints({
  endpoints: builder => ({
    getTwoFactorStatus: getTwoFactorStatus(builder, config),
    startTwoFactorEnrolment: startTwoFactorEnrolment(builder, config),
    confirmTwoFactorEnrolment: confirmTwoFactorEnrolment(builder, config),
    disableTwoFactor: disableTwoFactor(builder, config),
    forgetTwoFactorDevices: forgetTwoFactorDevices(builder, config),
    startTwoFactorReauth: startTwoFactorReauth(builder, config),
    verifyTwoFactorReauth: verifyTwoFactorReauth(builder, config),
    getRecoveryCodeCount: getRecoveryCodeCount(builder, config),
    regenerateRecoveryCodes: regenerateRecoveryCodes(builder, config),
  }),
});

export const {
  useGetTwoFactorStatusQuery,
  useStartTwoFactorEnrolmentMutation,
  useConfirmTwoFactorEnrolmentMutation,
  useDisableTwoFactorMutation,
  useForgetTwoFactorDevicesMutation,
  useStartTwoFactorReauthMutation,
  useVerifyTwoFactorReauthMutation,
  useGetRecoveryCodeCountQuery,
  useRegenerateRecoveryCodesMutation,
} = twoFactorApi;

export default twoFactorApi;
